import { Trans } from "@lingui/react/macro";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import type React from "react";
import { useEffect, useMemo } from "react";
import { Image, Link, Spacer } from "@heroui/react";
import { useSearchParams } from "react-router";
import { LimeLocalePicker } from "@/Components/NextBase/LimeLocalePicker";
import type { Locale } from "../../../../shared/constants/locales";
import LegalPages from "@/Components/NextBase/LegalPages";
import {
  getBrowserPreferredLocale,
  getClosestSupportedLocale,
  getLocaleSearchParam,
} from "@/utils/locale-utils";

type AuthFormWrapperProps = {
  isLogin: boolean;

  children: React.ReactNode;
};

export const AuthFormWrapper = ({
  isLogin,

  children,
}: AuthFormWrapperProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { preferredLanguage, setPreferredLanguage } = usePreferredLanguageStore(
    (state) => state,
  );

  const lang =
    getLocaleSearchParam(searchParams) ??
    getClosestSupportedLocale(preferredLanguage.userPreferredLanguage) ??
    getClosestSupportedLocale(preferredLanguage.clientPreferredLanguage) ??
    getBrowserPreferredLocale();

  const authSearchParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set("lang", lang);

    return params;
  }, [lang, searchParams]);

  useEffect(() => {
    if (searchParams.get("lang") === lang) return;

    setSearchParams(authSearchParams, { replace: true });
  }, [authSearchParams, lang, searchParams, setSearchParams]);

  return (
    <>
      <LimeLocalePicker
        value={lang}
        onChange={(locale: Locale) => {
          const params = new URLSearchParams(searchParams);
          params.set("lang", locale);
          setSearchParams(params, { replace: true });

          void setPreferredLanguage({
            userPreferredLanguage: locale,
            clientPreferredLanguage: locale,
          });
        }}
        className="fixed top-[calc(var(--safe-area-top,0px)+0.5rem)] right-[calc(var(--safe-area-right,0px)+0.5rem)] z-20 w-44"
        label={undefined}
        customOrder={["en", "sl", "hr", "sr", "bs", "de", "it", "es", "fr"]}
      />

      {/* 100dvh minus the safe-area top padding on body (so the page fits the
          viewport exactly and doesn't scroll in the Capacitor app), with the
          bottom padding extended past the iOS home indicator. */}
      <div className="flex min-h-[calc(100dvh-var(--safe-area-top,0px))] w-full flex-col items-center justify-center bg-white px-6 pt-20 pb-[calc(1.5rem+var(--safe-area-bottom,0px))] text-black md:pt-6">
        <div className="flex w-full max-w-lg flex-col items-center">
          <Image
            src="/lime-logo-full-horizontal.png"
            alt="logo"
            className="mb-4 h-auto w-72"
          />
          <p className="mb-1 text-center text-xl font-semibold md:text-2xl">
            {isLogin ? (
              <Trans>Prijava v Lime Booking račun</Trans>
            ) : (
              <Trans>Ustvari si uporabniški račun</Trans>
            )}
          </p>
          <p className="text-default-500 text-sm">
            {isLogin ? (
              <Trans>Dobrodošli nazaj!</Trans>
            ) : (
              <Trans>Preizkusi Lime Booking brezplačno!</Trans>
            )}
          </p>

          <Spacer y={8} />

          <div className="flex w-full flex-col gap-4">{children}</div>

          <Spacer y={8} />

          <p className="text-default-500 text-sm">
            <span className="pr-2">
              {isLogin ? (
                <Trans>Še nimaš računa?</Trans>
              ) : (
                <Trans>Si že registriran?</Trans>
              )}
            </span>
            {isLogin && (
              <Link
                data-identifier="register-redirect"
                className="text-primary text-sm font-semibold"
                href={`/register?${authSearchParams.toString()}`}
              >
                <Trans>Registriraj se</Trans>
              </Link>
            )}
          </p>
          <Link
            className="text-primary mt-1 text-sm font-medium"
            href={
              isLogin
                ? `/forgot-password?${authSearchParams.toString()}`
                : `/login?${authSearchParams.toString()}`
            }
          >
            {isLogin ? (
              <Trans>Si pozabil geslo?</Trans>
            ) : (
              <Trans>Prijavi se</Trans>
            )}
          </Link>

          <Spacer y={8} />

          <LegalPages language={lang} />
        </div>
      </div>
    </>
  );
};
